import { RefObject, useEffect, useState } from 'react';

export interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  callback: IntersectionObserverCallback;
  condition?: boolean;
  ref: RefObject<Element | null>;
  type?: 'both' | 'enter' | 'exit';
}

const useIntersectionObserver = ({
  callback,
  condition = true,
  ref,
  root = null,
  rootMargin = '0px',
  threshold = 0,
}: UseIntersectionObserverOptions): void => {
  const [isActive, setIsActive] = useState(condition);

  useEffect(() => {
    setIsActive(condition);
  }, [condition]);

  useEffect(() => {
    const node = ref?.current;
    const hasObserverSupport = Boolean(window.IntersectionObserver);
    if (!(node && hasObserverSupport && isActive)) return () => undefined;

    const observer = new IntersectionObserver((entries, obs) => callback(entries, obs), {
      threshold,
      root,
      rootMargin,
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [callback, ref, isActive, root, rootMargin, threshold]);
};

export default useIntersectionObserver;
